import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { useCallback, useImperativeHandle, useMemo, useRef } from 'react'
import type {
  Components as VirtuosoComponents,
  FlatIndexLocationWithAlign,
  FlatScrollIntoViewLocation,
  VirtuosoHandle,
  VirtuosoProps,
  IndexLocationWithAlign,
} from 'react-virtuoso'
import { Virtuoso } from 'react-virtuoso'

/* eslint-disable-next-line import/no-restricted-paths -- FIXME: UXP-4500 */
import useSize from '@src/lib/hooks/useSize'
import { theme } from '@ui/theme'

import type { OuterContextProps, VirtualListItem } from './Context'
import type Context from './Context'
import Item from './Item'
import List from './List'
import ScrollBar from './ScrollBar'
import ScrollToBottom from './ScrollToBottom'
import Scroller from './Scroller'
import * as styles from './VirtualList.css'
import useScrollerController from './useScrollerController'

const DEFAULT_INCREASE_VIEWPORT_BY = { top: 400, bottom: 400 }

const computeItemKey = (index: number, item: VirtualListItem): string => item.id

type Components<T extends VirtualListItem> = VirtuosoComponents<Context<T>>

export interface VirtualListHandle {
  /**
   * Scroll to a specific item in the list by its index.
   */
  scrollToIndex: (location: number | FlatIndexLocationWithAlign) => void

  /**
   * Scrolls the item into view if necessary.
   */
  scrollIntoView: (location: FlatScrollIntoViewLocation) => void
}

export type VirtualListProps<T extends VirtualListItem> = Pick<
  Required<VirtuosoProps<T, Context<T>>>,
  'data' | 'itemContent'
> &
  Pick<
    VirtuosoProps<T, Context<T>>,
    | 'firstItemIndex'
    | 'followOutput'
    | 'atBottomStateChange'
    | 'atBottomThreshold'
    | 'atTopStateChange'
    | 'atTopThreshold'
    | 'overscan'
    | 'increaseViewportBy'
    | 'itemsRendered'
    | 'totalListHeightChanged'
    | 'isScrolling'
  > & {
    /**
     * The handle for invoking imperative methods on the virtual list.
     */
    handleRef?: React.MutableRefObject<VirtualListHandle | null>

    /**
     * Enable or disable the scrollbar in the scrollable list.
     *
     * @default true
     */
    scrollbar?: boolean

    /**
     * Show a "scroll to bottom" button with the specified badge count.
     */
    scrollToBottomBadgeCount?: number

    /**
     * Function to call when the user presses the "scroll to bottom" button.
     *
     * By default, the list will scroll to the bottom, but we can override this behavior by passing
     * a function to this property.
     */
    scrollToBottom?: () => void

    /**
     * Called whenever a 'scroll' event is received.
     *
     * @param left The scrollLeft value of the list.
     * @param top The scrollTop value of the list.
     */
    onPassiveScroll?: (left: number, top: number) => void

    /**
     * Set to a value >= 0 to make the list start scrolled to that item.
     */
    initialTopMostItemIndex?: number | IndexLocationWithAlign

    'data-testid'?: string

    /**
     * Allows to further customize component props and behavior
     */
    context?: OuterContextProps

    /**
     * Allows to override the set of components that comprise the virtual scroll
     *
     * @see https://virtuoso.dev/virtuoso-api/interfaces/Components/
     */
    components?: Components<T>
  }

const VirtualList = function <T extends VirtualListItem>({
  handleRef,
  scrollbar = true,
  scrollToBottomBadgeCount,
  scrollToBottom,
  onPassiveScroll,
  atBottomStateChange,
  'data-testid': testId,
  context: outerContext,
  components: outerComponents,
  isScrolling,
  increaseViewportBy = DEFAULT_INCREASE_VIEWPORT_BY,
  ...props
}: VirtualListProps<T>) {
  const ref = useRef<HTMLDivElement>(null)
  const knobRef = useRef<HTMLDivElement>(null)
  const scrollerRef = useRef<HTMLElement>(null)
  const scrollToBottomButtonRef = useRef<HTMLDivElement>(null)
  const virtuosoHandle = useRef<VirtuosoHandle>(null)

  const controller = useScrollerController({
    knobBackground: `rgb(${theme.palette.gray[4]})`,
    knobHoverBackground: `rgb(${theme.palette.gray[3]})`,
    knobRef,
    onPassiveScroll,
    atBottomStateChange,
    scrollbar,
    scrollerRef,
    scrollToBottomButtonRef,
    virtuosoHandle,
  })

  const context: Context<T> = { controller, ...outerContext }
  const components = useMemo(
    () => ({ Scroller, List, Item, ...outerComponents }),
    [outerComponents],
  ) as VirtuosoComponents<T, Context<T>>

  const pointerSensor = useSensor(PointerSensor)
  const sensors = useSensors(pointerSensor)

  useImperativeHandle(
    handleRef,
    () => ({
      scrollToIndex: (location) => virtuosoHandle.current?.scrollToIndex(location),
      scrollIntoView: (location) => virtuosoHandle.current?.scrollIntoView(location),
    }),
    [],
  )

  useSize({
    ref,
    onResize: useCallback(
      ({ height }) => controller.handleViewportHeightChanged(height),
      [controller],
    ),
  })

  return (
    <div
      ref={ref}
      className={styles.root}
      {...controller.viewportListeners}
      data-testid={testId}
    >
      <Virtuoso
        {...props}
        {...controller.virtuosoProps}
        ref={virtuosoHandle}
        context={context}
        computeItemKey={computeItemKey}
        components={components}
        increaseViewportBy={increaseViewportBy}
        isScrolling={isScrolling}
        totalListHeightChanged={(height) => {
          props.totalListHeightChanged?.(height)
          controller.virtuosoProps.totalListHeightChanged?.(height)
        }}
      />
      {scrollbar ? (
        <DndContext
          autoScroll={false}
          modifiers={[restrictToVerticalAxis]}
          sensors={sensors}
          {...controller.dndListeners}
        >
          <ScrollBar controller={controller} knobRef={knobRef} />
        </DndContext>
      ) : null}
      {typeof scrollToBottomBadgeCount === 'number' ? (
        <ScrollToBottom
          controller={controller}
          badgeCount={scrollToBottomBadgeCount}
          onClick={() => {
            if (scrollToBottom) {
              scrollToBottom()
              return
            }

            virtuosoHandle.current?.scrollToIndex({
              index: props.data.length - 1,
              align: 'end',
            })
          }}
        />
      ) : null}
    </div>
  )
}

export default VirtualList
